/**
 * Gates clinician-report generation on the validated mood/anxiety screeners.
 *
 * If the patient has no stored PHQ-9 or GAD-7 score, `gate()` opens the
 * screener modal and resolves once they save or skip; if a score already
 * exists (or intake hasn't loaded yet), it resolves immediately so the report
 * flow is never blocked. Render the returned `modal` somewhere in the tree.
 *
 *   const { gate, modal } = useScreenerGate()
 *   async function handleGenerate() {
 *     if (!(await gate())) return   // patient dismissed — abort
 *     ...fetch the report...
 *   }
 *   return <>{...}{modal}</>
 */
import { useCallback, useRef, useState } from 'react'
import { useIntake } from '@pelvia/shared/hooks/patient/useIntake'
import { ScreenerGateModal } from '@/components/ScreenerGateModal'

export function useScreenerGate() {
  const { data } = useIntake()
  const loaded = data !== undefined
  const intake = data?.intake ?? null
  // Prompt only once intake has loaded and a screener total is genuinely
  // missing. A real score (including a valid 0) clears the gate for good.
  const needsScreener =
    loaded && (intake?.phq9_score == null || intake?.gad7_score == null)

  const [open, setOpen] = useState(false)
  const resolver = useRef<((proceed: boolean) => void) | null>(null)

  const gate = useCallback((): Promise<boolean> => {
    if (!needsScreener) return Promise.resolve(true)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [needsScreener])

  const finish = useCallback((proceed: boolean) => {
    setOpen(false)
    resolver.current?.(proceed)
    resolver.current = null
  }, [])

  const modal = (
    <ScreenerGateModal
      open={open}
      onSaved={() => finish(true)}
      onSkip={() => finish(true)}
      onCancel={() => finish(false)}
    />
  )

  return { gate, modal }
}
